import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useNotificationEvents } from '../live/LiveContext';
import Icon from './Icon';

function ago(iso) {
  if (!iso) return '';
  const t = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  const min = Math.round((Date.now() - t) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  if (min < 48 * 60) return `${Math.round(min / 60)} h ago`;
  return new Date(t).toLocaleDateString();
}

const canDesktop = () => typeof window !== 'undefined' && 'Notification' in window;

/** Top-bar bell: notes written on interviews this admin can see, live (pop-up + optional desktop notification). */
function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [permission, setPermission] = useState(() => (canDesktop() ? Notification.permission : 'unsupported'));
  const boxRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/notifications');
      setItems(res.data.notifications || []);
      setUnread(res.data.unread || 0);
    } catch {
      /* the bell just stays as it was */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openLink = useCallback((link) => {
    if (link) navigate(link);
  }, [navigate]);

  useNotificationEvents((e) => {
    load();
    if (!e || !e.title) return; // a read-state sync, not a new notification
    const id = `${e.seq}`;
    setToasts((prev) => [...prev.slice(-2), { id, title: e.title, body: e.body, link: e.link }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 9000);
    if (canDesktop() && Notification.permission === 'granted') {
      try {
        const n = new Notification(e.title, { body: e.body || '', tag: id });
        n.onclick = () => {
          window.focus();
          openLink(e.link);
          n.close();
        };
      } catch {
        /* some browsers only allow notifications from a service worker */
      }
    }
  });

  useEffect(() => {
    if (!open) return undefined;
    const close = (ev) => {
      if (boxRef.current && !boxRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const markRead = async (ids) => {
    try {
      await api.post('/api/notifications/read', { ids });
    } catch {
      /* stays unread */
    }
    load();
  };

  const clickItem = (n) => {
    if (!n.read) markRead([n.id]);
    setOpen(false);
    openLink(n.link);
  };

  const enableDesktop = async () => {
    if (!canDesktop()) return;
    setPermission(await Notification.requestPermission());
  };

  return (
    <div className="notif" ref={boxRef}>
      <button
        type="button"
        className="topbar-icon-btn notif-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread ? `${unread} unread notifications` : 'Notifications'}
        title="Notifications"
      >
        <Icon name="bell" size={18} />
        {unread > 0 && <span className="notif-count">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="notif-panel" role="dialog" aria-label="Notifications">
          <div className="notif-head">
            <strong>Notifications</strong>
            {unread > 0 && <button type="button" className="btn btn-link btn-sm" onClick={() => markRead(null)}>Mark all read</button>}
          </div>
          {permission === 'default' && (
            <button type="button" className="notif-desktop" onClick={enableDesktop}>
              Show desktop notifications too
            </button>
          )}
          {items.length === 0 ? (
            <p className="notif-empty">No notifications yet. Notes written on your interviews show up here.</p>
          ) : (
            <ul className="notif-list">
              {items.map((n) => (
                <li key={n.id}>
                  <button type="button" className={`notif-item${n.read ? '' : ' is-unread'}`} onClick={() => clickItem(n)}>
                    <span className="notif-title">{n.title}</span>
                    {n.body && <span className="notif-body">{n.body}</span>}
                    <span className="notif-time">{ago(n.created_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="notif-toasts" aria-live="polite">
        {!open && toasts.map((t) => (
          <button
            key={t.id}
            type="button"
            className="notif-toast"
            onClick={() => {
              setToasts((prev) => prev.filter((x) => x.id !== t.id));
              openLink(t.link);
            }}
          >
            <span className="notif-title"><Icon name="bell" size={14} /> {t.title}</span>
            {t.body && <span className="notif-body">{t.body}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default NotificationBell;
